import type React from 'react';
import { useMemo } from 'react';
import { useUserStore } from '@Auth/store';
import { useEntityStore } from '@ContractBuilder/store';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { groupByParent } from '@helpers/groupBlocksByParent';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useBlocks } from '@queries/fetchBlocks';
import { useAllTeams } from '@queries/fetchTeams';
import type { Action, PlainAction } from '@root/@types/types';
import { isNonNullish } from '@root/helpers';
import MODALS from '@root/src/constants/modals';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { useEffectOnce } from '@src/hooks';

import withMenuLayout from '../../layout/withMenuLayout';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { ListTypesToggle } from './components/ListTypesToggle';
import { getBlocksTableColumns } from './helpers/columns/getBlocksTableColumns';
import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

const initialFilters = [{ id: 'deleted_at', value: ['Active'] }];
const initialColumnsVisibility = {
  class_of_business: false,
  created_by: false,
  created_at: false,
  id: false,
  inception_date: false,
  last_updated_by: false,
  teams: false,
  title: false,
  toggleSubRows: false,
};

export const Blocks: React.FC = () => {
  const { resetState } = useEntityStore(({ resetState }) => ({ resetState }));
  const { user } = useUserStore(({ user }) => ({ user }));
  const { showModal } = useModalDispatch();

  const { data, status } = useBlocks();
  const { data: teams = [], isLoading: isTeamsLoading } = useAllTeams();

  const isEmpty = useDataIsEmpty(data, status);

  const handleCreateBlockClick = () => {
    showModal(MODALS.CREATE_LIBRARY_BLOCK, {});
  };

  useEffectOnce(() => {
    resetState();
  });

  const columns = getBlocksTableColumns({ user, teams });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New Block',
    onClick: handleCreateBlockClick,
  };

  const actions: Action[] = [
    createEntityAction,
    isPPLFrontend ? undefined : <DownloadCSVButton key="download-csv" type="blocks" />,
  ].filter(isNonNullish);
  const dataGroupedByParents = useMemo(() => groupByParent(data), [data]);

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={initialColumnsVisibility}
      columnPinning={{ right: ['edit'] }}
      createEntityAction={createEntityAction}
      entityName="block"
      filteredData={data as LibraryBlockTypeForListPage[]}
      expandableData={dataGroupedByParents ?? []}
      initialFilters={initialFilters}
      initialSorting={[{ id: 'updated_at', desc: true }]}
      status={status}
      renderCustomElements={(isGroupByParent, setIsGroupByParent) =>
        function toggleListTypes() {
          return <ListTypesToggle isGroupByParent={isGroupByParent} setIsGroupByParent={setIsGroupByParent} />;
        }
      }
      isEmpty={isEmpty}
      isLoading={isTeamsLoading}
    />
  );
};

export const UserBlocksPage = withMenuLayout(Blocks);
